import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OutsideClickHandler from 'react-outside-click-handler';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Wrap, GearButton, Popover, Title, List, Item, DragHandle, Footer, ResetLink } from './grid-columns-setup.styled';

/**
 * one draggable row in the columns settings list
 */
const SortableItem = ({ id, title, active, forced, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: forced });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Item ref={setNodeRef} style={style} $dragging={isDragging} $forced={forced}>
      <DragHandle {...(forced ? {} : { ...attributes, ...listeners })} title={forced ? '' : 'Изменить порядок'}>
        ⠿
      </DragHandle>
      <label>
        <input type="checkbox" checked={active} disabled={forced} onChange={() => onToggle(id)} />
        {title}
      </label>
    </Item>
  );
};

/**
 * "gear" button + popover for managing table columns (visibility, order).
 * column width is adjusted directly on the table header by dragging its edge.
 */
export const GridColumnsSetup = ({ columns, onToggle, onReorder, onReset }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const popoverRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const el = popoverRef.current;
    if (!el) {
      return;
    }
    setFlip(el.getBoundingClientRect().bottom > window.innerHeight);
  }, [open]);

  /**
   * toggle setup
   * @returns
   */
  const toggle = () => {
    setFlip(false);
    setOpen((val) => !val);
  };

  /**
   * close setup
   */
  const close = (event) => {
    if (event.target.closest('.toggle-popup')) {
      return;
    }
    setOpen(false);
    setFlip(false);
  };

  /**
   * drag end
   * @param {*} event
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(active.id, over.id);
    }
  };

  return (
    <Wrap>
      <GearButton className="toggle-popup" type="button" onClick={toggle} title={t('grid.Настроить колонки')}>
        <img src="/images/settings.svg" alt="*" />
      </GearButton>

      {open && (
        <OutsideClickHandler onOutsideClick={close}>
          <Popover ref={popoverRef} $flip={flip}>
            <Title>{t('grid.Настройка колонок')}</Title>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={columns.map((el) => el.name)} strategy={verticalListSortingStrategy}>
                <List>
                  {columns.map((col) => (
                    <SortableItem key={col.name} id={col.name} title={col.title} active={col.active} forced={col.forced} onToggle={onToggle} />
                  ))}
                </List>
              </SortableContext>
            </DndContext>

            <Footer>
              <ResetLink type="button" onClick={onReset}>
                {t('grid.Сбросить настройки')}
              </ResetLink>
            </Footer>
          </Popover>
        </OutsideClickHandler>
      )}
    </Wrap>
  );
};
